import { Component } from '@theme/component';

/**
 * DAVIDsTEA homepage hero — carousel of full-width slides.
 *
 * The rail is a scroll-snap container, so a finger, a trackpad and the keyboard all
 * work before this file loads. The script only adds what scrolling cannot give on its
 * own: the dots, the two arrows, and the autoplay.
 *
 * Controls stay hidden until this runs, and hide again whenever a single slide leaves
 * the rail nothing to scroll — otherwise a one-slide hero would ship with an inert pair
 * of arrows and a single dot.
 *
 * The step is measured between the first two slides rather than assumed to be the rail
 * width. Slides are full-width today, but reading the real offset keeps the arrows
 * honest if the stylesheet ever insets them.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [dots]
 *
 * @extends {Component<Refs>}
 */
export class DtHeroCarousel extends Component {
  requiredRefs = ['rail'];

  connectedCallback() {
    super.connectedCallback();

    const { rail, prev, next, dots } = this.refs;

    prev?.addEventListener('click', this.#onPrev);
    next?.addEventListener('click', this.#onNext);
    dots?.addEventListener('click', this.#onDotClick);
    rail.addEventListener('scroll', this.#onScroll, { passive: true });

    // Autoplay is a courtesy, not a hijack: any pointer or keyboard attention stops it,
    // and it only resumes once attention leaves.
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#observer = new ResizeObserver(this.#sync);
    this.#observer.observe(rail);

    this.#sync();
    this.setAttribute('data-ready', '');
    this.#resume();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    const { rail, prev, next, dots } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    dots?.removeEventListener('click', this.#onDotClick);
    rail?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#observer?.disconnect();
    this.#pause();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #timer;

  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.refs.rail.querySelectorAll('.dt-hero-carousel__slide')]);
  }

  /** One slide plus one gap, read from the DOM rather than assumed to be the rail width. */
  get #step() {
    const [first, second] = this.#slides;
    if (!first) return 0;
    if (second) return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    return first.getBoundingClientRect().width;
  }

  get #index() {
    const step = this.#step;
    if (!step) return 0;
    return Math.round(this.refs.rail.scrollLeft / step);
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** @param {number} index */
  #goTo(index) {
    const step = this.#step;
    const count = this.#slides.length;
    if (!step || !count) return;
    // Wraps both ways so the arrows never dead-end and autoplay can loop.
    const target = ((index % count) + count) % count;
    // A wrap is a jump, not a journey: smooth-scrolling from the last slide back to the
    // first drags the whole rail past the reader's eyes, which reads as a glitch rather
    // than as a loop. Only steps between neighbours animate.
    const wrapped = Math.abs(target - this.#index) > 1;
    this.refs.rail.scrollTo({ left: step * target, behavior: wrapped ? 'auto' : this.#behavior });
  }

  #onPrev = () => {
    this.#goTo(this.#index - 1);
    this.#restart();
  };

  #onNext = () => {
    this.#goTo(this.#index + 1);
    this.#restart();
  };

  /** @param {Event} event */
  #onDotClick = (event) => {
    const dot = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    if (!(dot instanceof HTMLElement)) return;
    this.#goTo(Number(dot.dataset.index));
    this.#restart();
  };

  #onScroll = () => this.#sync();

  #onVisibility = () => (document.hidden ? this.#pause() : this.#resume());

  #pause = () => {
    clearInterval(this.#timer);
    this.#timer = undefined;
  };

  #resume = () => {
    this.#pause();
    // A hero that cannot scroll has nothing to advance to, and a reader who asked for
    // reduced motion did not ask for a slideshow that moves on its own.
    if (this.dataset.autoplay !== 'true') return;
    if (this.#slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const delay = Number(this.dataset.delay || 5) * 1000;
    this.#timer = setInterval(() => this.#goTo(this.#index + 1), delay);
  };

  #restart = () => {
    // Advancing by hand should buy a full delay before the next automatic move,
    // not land the reader mid-interval on a slide they just chose.
    if (this.#timer) this.#resume();
  };

  /**
   * A single pixel of slack on each end: browsers land on fractional scroll offsets, and
   * an exact comparison marks the wrong dot at the last stop.
   */
  #sync = () => {
    const { rail, dots } = this.refs;
    const scrollable = rail.scrollWidth - rail.clientWidth > 1;
    this.toggleAttribute('data-scrollable', scrollable);

    const index = this.#index;
    this.#slides.forEach((slide, i) => {
      // Off-screen slides stay out of the tab order and out of the accessibility tree,
      // so a keyboard reader is not walked through three hidden buttons.
      slide.toggleAttribute('inert', scrollable && i !== index);
      slide.setAttribute('aria-hidden', String(scrollable && i !== index));
    });

    if (!dots) return;
    [...dots.querySelectorAll('[data-index]')].forEach((dot, i) => {
      dot.toggleAttribute('data-current', i === index);
      dot.setAttribute('aria-current', String(i === index));
    });
  };
}

if (!customElements.get('dt-hero-carousel-component')) {
  customElements.define('dt-hero-carousel-component', DtHeroCarousel);
}
